import { useEffect, useId, useRef, useState } from 'react';
import type { PersonaPanel } from '../lib/snapshot-types.js';
import { SIN_ASIGNAR, normalizarSeleccion, type SeleccionPersonas } from '../lib/filtro-panel.js';
import { colorDe, iniciales } from '../lib/avatar.js';

/**
 * EL DESPLEGABLE DE PERSONAS (sustituye a "Yo / Equipo", revamp del 6-oct).
 *
 * Lista los nombres que trae el snapshot (`snapshot.personas`: los usuarios del
 * inquilino con su nombre de GHL, y "Sin asignar" si hay actividad sin persona).
 * Se marcan las que se quieren ver; siempre queda al menos una. Marcarlas todas
 * una a una es "Todas" (la misma suma). Se usa con teclado: el boton abre, cada
 * persona es una casilla nativa (Tab y espacio) y Escape cierra.
 *
 * No guarda nada: quien lo usa (App) recuerda lo elegido por quien mira.
 */

interface Props {
  personas: readonly PersonaPanel[];
  /** Lo elegido (se normaliza contra la lista: lo que no esta no cuenta). */
  seleccion: SeleccionPersonas;
  onChange: (sel: SeleccionPersonas) => void;
}

/** Las personas que se ven en el desplegable: la lista, y "Sin asignar" si esta elegida aunque hoy no salga. */
function filas(personas: readonly PersonaPanel[], sel: SeleccionPersonas): PersonaPanel[] {
  const out = [...personas];
  if (sel !== 'todas' && sel.includes(SIN_ASIGNAR) && !out.some((p) => p.id === SIN_ASIGNAR)) {
    out.push({ id: SIN_ASIGNAR, nombre: 'Sin asignar', yo: false });
  }
  return out;
}

/** Lo que dice el boton: "Todas", el nombre de la persona o "N personas". */
export function rotuloSeleccion(sel: SeleccionPersonas, personas: readonly PersonaPanel[]): string {
  const n = normalizarSeleccion(sel, personas);
  if (n === 'todas') return 'Todas';
  if (n.length === 1) return filas(personas, n).find((p) => p.id === n[0])?.nombre ?? '1 persona';
  return `${n.length} personas`;
}

/**
 * Marcar o desmarcar una persona. Desde "Todas", desmarcar a alguien deja a las
 * demas. La ultima marcada no se puede quitar (no se puede ver a nadie).
 */
export function alternarPersona(sel: SeleccionPersonas, id: string, personas: readonly PersonaPanel[]): SeleccionPersonas {
  const lista = filas(personas, sel);
  const n = normalizarSeleccion(sel, personas);
  const marcadas = n === 'todas' ? lista.map((p) => p.id) : n;
  const siguiente = marcadas.includes(id) ? marcadas.filter((x) => x !== id) : [...marcadas, id];
  if (siguiente.length === 0) return n;
  return normalizarSeleccion(siguiente, personas);
}

function Avatar({ persona }: { persona: PersonaPanel }) {
  if (persona.id === SIN_ASIGNAR) return <span className="av av--nadie" aria-hidden="true">?</span>;
  return <span className={`av av--${colorDe(persona.nombre)}`} aria-hidden="true">{iniciales(persona.nombre)}</span>;
}

export function PersonasSelect({ personas, seleccion, onChange }: Props) {
  const [abierto, setAbierto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const boton = useRef<HTMLButtonElement>(null);
  const id = useId();
  const sel = normalizarSeleccion(seleccion, personas);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent): void => {
      if (raiz.current && !raiz.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const cerrar = (): void => { setAbierto(false); boton.current?.focus(); };

  return (
    <div
      className="ps"
      ref={raiz}
      onKeyDown={(e) => { if (e.key === 'Escape' && abierto) { e.stopPropagation(); cerrar(); } }}
    >
      <button
        ref={boton}
        type="button"
        className={`ps-boton${abierto ? ' abierto' : ''}`}
        aria-haspopup="true"
        aria-expanded={abierto}
        aria-controls={id}
        onClick={() => setAbierto((a) => !a)}
      >
        <svg className="ps-icono" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
          <circle cx="6" cy="5.5" r="2.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path d="M1.5 13.5c.6-2.4 2.3-3.6 4.5-3.6s3.9 1.2 4.5 3.6" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          <path d="M10.8 3.4a2.4 2.4 0 0 1 0 4.4M12.4 10.2c1 .6 1.7 1.7 2 3.3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <span className="ps-boton-rotulo">Personas:</span>
        <strong className="ps-boton-valor">{rotuloSeleccion(sel, personas)}</strong>
        <svg className="ps-flecha" viewBox="0 0 10 10" width="9" height="9" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {abierto && <MenuPersonas id={id} personas={personas} seleccion={sel} onChange={onChange} />}
    </div>
  );
}

/**
 * Las casillas del desplegable (abierto). Aparte para poder comprobar su marcado
 * sin un navegador: "Todas", y cada persona con su avatar y su nombre.
 */
export function MenuPersonas({ id, personas, seleccion, onChange }: Props & { id?: string }) {
  const sel = normalizarSeleccion(seleccion, personas);
  const lista = filas(personas, sel);
  const unaSola = sel !== 'todas' && sel.length === 1;
  return (
    <div className="ps-menu" id={id} role="group" aria-label="Personas que se ven">
      <label className="ps-opcion ps-opcion--todas">
        <input type="checkbox" checked={sel === 'todas'} disabled={sel === 'todas'} onChange={() => onChange('todas')} />
        <span className="av av--todas" aria-hidden="true">{lista.length}</span>
        <span className="ps-nombre">Todas</span>
      </label>
      <div className="ps-sep" role="presentation" />
      {lista.map((p) => {
        const si = sel === 'todas' || sel.includes(p.id);
        const ultima = si && unaSola;
        return (
          <label key={p.id} className="ps-opcion" title={ultima ? 'Siempre queda al menos una' : undefined}>
            <input type="checkbox" checked={si} disabled={ultima} onChange={() => onChange(alternarPersona(sel, p.id, personas))} />
            <Avatar persona={p} />
            <span className="ps-nombre">{p.nombre}</span>
            {p.yo && <span className="ps-tu">tú</span>}
          </label>
        );
      })}
    </div>
  );
}
